// AIConsultantDrawer — o drawer lateral do Consultor IA.
//
// Cabeçalho contextual (Estamos construindo X + impactos + tempo),
// histórico de mensagens, composer com submit por Enter e sugestões
// aplicáveis renderizadas inline.

import { useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useMutation } from "@tanstack/react-query";
import { Loader2, Send, Sparkles, X, Clock, TrendingUp, User } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { AISuggestionCard } from "./AISuggestionCard";
import {
  getAIConversation,
  sendAIMessage,
  startAIConversation,
  type AIConversationMessage,
  type AIConversationView,
} from "@/lib/ai/consultant.functions";
import { submitHomologationFeedback } from "@/lib/ai/homologation.functions";
import type { BlockKey } from "@/lib/business-blocks";

const ONBOARDING_KEY = "ai_consultant_onboarding_v1";

export function AIConsultantDrawer({
  isOpen,
  onClose,
  sectionKey,
  fieldKey,
  conversationId,
  intent,
}: {
  isOpen: boolean;
  onClose: () => void;
  sectionKey?: BlockKey | null;
  fieldKey?: string | null;
  conversationId?: string | null;
  intent?: "conversation" | "field_suggestion" | "review";
}) {
  const [view, setView] = useState<AIConversationView | null>(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showExitSurvey, setShowExitSurvey] = useState(false);
  const [exitRating, setExitRating] = useState<"very_useful" | "useful" | "slightly_useful" | "not_useful" | null>(null);
  const [exitComment, setExitComment] = useState("");
  const messagesCountRef = useRef(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const start = useServerFn(startAIConversation);
  const send = useServerFn(sendAIMessage);
  const load = useServerFn(getAIConversation);
  const sendFeedback = useServerFn(submitHomologationFeedback);

  // Boot: quando abrir, ou inicia nova conversa ou carrega existente.
  useEffect(() => {
    if (!isOpen) return;
    setView(null);
    setInput("");
    setLoading(true);
    setShowExitSurvey(false);
    setExitRating(null);
    setExitComment("");
    // Onboarding once per browser.
    try {
      if (typeof window !== "undefined" && !window.localStorage.getItem(ONBOARDING_KEY)) {
        setShowOnboarding(true);
      }
    } catch { /* noop */ }
    (async () => {
      try {
        if (conversationId) {
          const v = await load({ data: { conversationId } });
          setView(v);
        } else {
          const v = await start({
            data: {
              sectionKey: (sectionKey ?? null) as BlockKey | null,
              fieldKey: fieldKey ?? null,
              intent,
            },
          });
          setView(v);
        }
      } catch (e: any) {
        toast.error(e?.message ?? "Não foi possível abrir o consultor.");
        onClose();
      } finally {
        setLoading(false);
        setTimeout(() => inputRef.current?.focus(), 60);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, conversationId, sectionKey, fieldKey, intent]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    if (view?.messages) messagesCountRef.current = view.messages.length;
  }, [view?.messages.length, sending]);

  function dismissOnboarding() {
    try { window.localStorage.setItem(ONBOARDING_KEY, "1"); } catch { /* noop */ }
    setShowOnboarding(false);
    setTimeout(() => inputRef.current?.focus(), 40);
  }

  function handleCloseRequest() {
    // Pede exit survey só se houve troca real (≥3 mensagens do usuário+assistente).
    if (messagesCountRef.current >= 3 && !showExitSurvey) {
      setShowExitSurvey(true);
      return;
    }
    onClose();
  }

  async function submitExitSurvey(skip: boolean) {
    if (!skip && exitRating) {
      try {
        await sendFeedback({
          data: {
            trigger: "exit_survey",
            rating: exitRating,
            comment: exitComment.trim() || null,
          },
        });
      } catch { /* silencioso */ }
    }
    setShowExitSurvey(false);
    onClose();
  }

  async function handleSend() {
    if (!view || !input.trim() || sending) return;
    const text = input.trim();
    setInput("");
    setSending(true);
    // otimista
    const optimistic: AIConversationMessage = {
      id: `tmp-${Date.now()}`,
      role: "user",
      content: text,
      createdAt: new Date().toISOString(),
      suggestion: null,
    };
    setView((v) => (v ? { ...v, messages: [...v.messages, optimistic] } : v));
    try {
      const res = await send({ data: { conversationId: view.conversationId, text } });
      setView((v) => {
        if (!v) return v;
        const withoutOptimistic = v.messages.filter((m) => m.id !== optimistic.id);
        return { ...v, messages: [...withoutOptimistic, res.userMessage, res.assistantMessage] };
      });
      setTimeout(() => inputRef.current?.focus(), 40);
    } catch (e: any) {
      // remove otimista
      setView((v) => (v ? { ...v, messages: v.messages.filter((m) => m.id !== optimistic.id) } : v));
      setInput(text);
      toast.error(e?.message ?? "Não foi possível enviar.");
    } finally {
      setSending(false);
    }
  }

  const ctx = view?.context;
  const section = ctx?.section;

  return (
    <Sheet open={isOpen} onOpenChange={(o) => !o && handleCloseRequest()}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-[520px]"
      >
        {/* Onboarding overlay */}
        {showOnboarding && (
          <div className="absolute inset-0 z-30 flex items-center justify-center bg-white/95 p-6">
            <div className="max-w-sm text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-[var(--navy)] text-white">
                <Sparkles size={20} />
              </div>
              <p className="mt-4 font-display text-lg font-extrabold text-[var(--navy)]">
                Bem-vindo ao Consultor IA
              </p>
              <ul className="mt-3 space-y-2 text-left text-[13px] leading-snug text-[var(--gray-ink)]">
                <li>• Ele conhece o seu Plano e propõe sugestões contextuais.</li>
                <li>• Você revisa antes de aplicar — nada muda sem sua confirmação.</li>
                <li>• Suas respostas ajudam a homologar o consultor (opcional).</li>
              </ul>
              <button
                onClick={dismissOnboarding}
                className="mt-5 inline-flex items-center justify-center rounded-xl bg-[var(--navy)] px-4 py-2 text-sm font-semibold text-white"
              >
                Entendi, começar
              </button>
            </div>
          </div>
        )}

        {/* Exit survey overlay */}
        {showExitSurvey && (
          <div className="absolute inset-0 z-30 flex items-center justify-center bg-white/95 p-6">
            <div className="w-full max-w-sm">
              <p className="font-display text-base font-extrabold text-[var(--navy)]">
                O consultor foi útil nesta conversa?
              </p>
              <p className="mt-1 text-[12px] text-[var(--gray-ink)]">
                Sua resposta é anônima e ajuda a homologar a IA.
              </p>
              <div className="mt-3 grid grid-cols-2 gap-2">
                {([
                  ["very_useful", "Muito útil"],
                  ["useful", "Útil"],
                  ["slightly_useful", "Pouco útil"],
                  ["not_useful", "Não foi útil"],
                ] as const).map(([k, label]) => (
                  <button
                    key={k}
                    onClick={() => setExitRating(k)}
                    className={`rounded-lg border px-3 py-2 text-[12px] font-semibold ${
                      exitRating === k
                        ? "border-[var(--navy)] bg-[var(--navy)] text-white"
                        : "border-black/10 bg-white text-[var(--navy)] hover:bg-black/[0.03]"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <textarea
                value={exitComment}
                onChange={(e) => setExitComment(e.target.value)}
                placeholder="Algum comentário? (opcional)"
                rows={2}
                className="mt-3 w-full resize-none rounded-lg border border-black/10 bg-white p-2 text-sm outline-none"
              />
              <div className="mt-3 flex items-center justify-end gap-2">
                <button
                  onClick={() => submitExitSurvey(true)}
                  className="rounded-lg px-3 py-2 text-[12px] font-semibold text-[var(--gray-ink)] hover:text-[var(--navy)]"
                >
                  Pular
                </button>
                <button
                  onClick={() => submitExitSurvey(false)}
                  disabled={!exitRating}
                  className="rounded-lg bg-[var(--navy)] px-3 py-2 text-[12px] font-semibold text-white disabled:opacity-50"
                >
                  Enviar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="border-b border-black/5 bg-gradient-to-br from-[var(--navy)]/[0.05] to-white px-5 pb-4 pt-5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[var(--navy)] text-white">
                <Sparkles size={16} />
              </div>
              <div>
                <p className="font-display text-base font-extrabold text-[var(--navy)]">
                  Construir com IA
                </p>
                <p className="text-[11px] text-[var(--gray-ink)]">
                  Estou ajudando você a construir esta parte do seu negócio.
                </p>
              </div>
            </div>
            {/* Botão de fechar é fornecido pelo SheetContent (canto superior direito) */}
          </div>


          {section && (
            <div className="mt-3 rounded-xl border border-black/5 bg-white/70 p-3">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--gray-ink)]">
                Estamos construindo
              </p>
              <p className="mt-0.5 font-display text-sm font-extrabold text-[var(--navy)]">
                {view?.title ?? section.title}
              </p>
              {section.impactedTitles.length > 0 && (
                <p className="mt-2 flex flex-wrap items-center gap-1 text-[11px] text-[var(--gray-ink)]">
                  <TrendingUp size={11} className="text-[var(--gold)]" />
                  Impacta: {section.impactedTitles.slice(0, 4).join(", ")}
                </p>
              )}
              <p className="mt-1 flex items-center gap-1 text-[11px] text-[var(--gray-ink)]">
                <Clock size={11} /> {section.estimatedMinutes} min estimados
              </p>
            </div>
          )}

          {view?.summary && (view.summary.goal || view.summary.confirmedFacts.length > 0 || view.summary.nextStep) && (
            <div className="mt-3 rounded-xl border border-black/5 bg-[var(--navy)]/[0.03] p-3">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--gray-ink)]">
                Retomando de onde paramos · v{view.summary.version}
              </p>
              {view.summary.goal && (
                <p className="mt-1 text-[12px] leading-snug text-[var(--navy)]">
                  <strong>Objetivo:</strong> {view.summary.goal}
                </p>
              )}
              {view.summary.confirmedFacts.length > 0 && (
                <p className="mt-1 text-[11px] leading-snug text-[var(--gray-ink)]">
                  <strong>Decisões:</strong> {view.summary.confirmedFacts.slice(0, 3).join(" · ")}
                </p>
              )}
              {view.summary.nextStep && (
                <p className="mt-1 text-[11px] leading-snug text-[var(--gray-ink)]">
                  <strong>Próximo passo:</strong> {view.summary.nextStep}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-[var(--gray-ink)]">
              <Loader2 size={14} className="animate-spin" /> Preparando o consultor…
            </div>
          )}
          {view?.messages.map((m) => (
            <MessageBubble key={m.id} message={m} conversationId={view.conversationId} />
          ))}
          {sending && (
            <div className="flex items-center gap-2 text-xs text-[var(--gray-ink)]">
              <Loader2 size={12} className="animate-spin" /> O consultor está pensando…
            </div>
          )}
        </div>

        {/* Composer */}
        <div className="border-t border-black/5 bg-white p-3">
          <div className="flex items-end gap-2 rounded-xl border border-black/10 bg-white p-2 focus-within:border-[var(--navy)]/40">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder="Escreva sua resposta ou pergunta…"
              rows={2}
              disabled={sending || loading}
              className="flex-1 resize-none border-0 bg-transparent text-sm outline-none placeholder:text-[var(--gray-ink)]/60 disabled:opacity-60"
            />
            <button
              type="button"
              onClick={handleSend}
              disabled={sending || loading || !input.trim()}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[var(--navy)] text-white shadow-sm transition hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0"
              aria-label="Enviar"
            >
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            </button>
          </div>
          <p className="mt-1.5 px-1 text-[10px] text-[var(--gray-ink)]">
            Enter envia · Shift+Enter quebra linha
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function MessageBubble({ message, conversationId }: { message: AIConversationMessage; conversationId?: string | null }) {
  if (message.role === "user") {
    return (
      <div className="flex justify-end gap-2">
        <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-[var(--navy)] px-3.5 py-2 text-sm text-white">
          {message.content}
        </div>
        <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--navy)]/10 text-[var(--navy)]">
          <User size={12} />
        </div>
      </div>
    );
  }
  return (
    <div className="flex gap-2">
      <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--navy)] text-white">
        <Sparkles size={12} />
      </div>
      <div className="max-w-[92%] space-y-2">
        {message.content && (
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-[var(--navy)]">
            {message.content}
          </p>
        )}
        {message.suggestion && (
          <AISuggestionCard
            suggestionId={message.suggestion.id}
            text={message.suggestion.text}
            confidence={message.suggestion.confidence}
            warnings={message.suggestion.warnings}
            conversationId={conversationId ?? null}
            sectionKey={message.suggestion.sectionKey}
            fieldKey={message.suggestion.fieldKey}
          />
        )}
      </div>
    </div>
  );
}
